import { clearDataset, saveDataset } from "@/api";
import AdjustmentSteps from "@/components/AdjustmentSteps";
import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import BackToDashboard from "@/components/BackToDashboard";
import CsvUpload, { CsvResult } from "@/components/CsvUpload";
import LoadingCard from "@/components/LoadingCard";
import { PropertyHeader } from "@/components/PropertyHeader";
import SheetTable from "@/components/SheetTable";
import StepTitleSection from "@/components/StepTitleSection";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import useFetch from "@/hooks/useFetch";
import { sheetToFileV1Data } from "@/lib/datasetVersions/v1";
import { sheetToFileV2Data } from "@/lib/datasetVersions/v2";
import { autoformatFields, validateDatasetBasedOnVersion } from "@/lib/dataValidation";
import { detectDatasetVersion, genIndexesOnSheet, Sheet } from "@/lib/PreparedDataset";
import { cn } from "@/lib/utils";
import { PageProps } from "@/types/PageProps";
import Adjustment from "@/types/serializers/Adjustment";
import { Head, router, useForm } from "@inertiajs/react";
import { ArrowRightIcon } from "lucide-react";
import posthog from "posthog-js";
import { useMemo } from "react";
import { GrHelpBook } from "react-icons/gr";

type Props = PageProps & {
  adjustment: Adjustment;
  direct_upload_url: string;
  dataset_url: string | null;
};

function FileFormatRequirements() {
  return (
    <div className="pt-4 space-y-4">
      <h2 className="text-md font-bold">File format requirements</h2>
      <ul className="text-sm list-disc space-y-2 [&>li]:ml-4">
        <li>File must be in .csv format.</li>

        <li>Files cannot contain more data fields than the ones listed below.</li>

        <li>
          If your MLS has <strong>separate fields for address number and street name</strong>, then
          use{" "}
          <a
            href="/sample-4col.csv"
            target="_blank"
            className="underline hover:bg-muted -mx-1 px-1 rounded"
          >
            this .csv file template
          </a>
          , which has the following four columns:
          <ul className="list-circle [&>li]:ml-6">
            <li>House Number</li>
            <li>Street Name</li>
            <li>Contract Date</li>
            <li>Close Price</li>
          </ul>
        </li>

        <li>
          If your MLS has a <strong>single address field</strong>, then use{" "}
          <a
            href="/sample-3col.csv"
            target="_blank"
            className="underline hover:bg-muted -mx-1 px-1 rounded"
          >
            this .csv file template
          </a>
          , which has the following three columns:
          <ul className="list-circle [&>li]:ml-6">
            <li>Address</li>
            <li>Contract Date</li>
            <li>Close Price</li>
          </ul>
        </li>
      </ul>
    </div>
  );
}

function SelectingDataTips() {
  return (
    <div className="pt-4 space-y-4">
      <h2 className="text-md font-bold">Choosing the right data</h2>
      <ul className="text-sm list-disc space-y-2 [&>li]:ml-4">
        <li>
          <strong>Search criteria</strong> - Data to find a market trend does not need to consist of
          only perfect comparable properties. You are the expert in your market - consider homes
          that attract a similar buyer to the subject instead of just homes that have the same
          stats.
        </li>
        <li>
          <strong>Time range</strong> - We recommend 13 months of data, but you should include data
          dating back at least 2 months prior to your oldest contract date.
        </li>
        <li>
          <strong>Quantity</strong> - The tool needs many properties to find a good trend line - the
          more data the better. If you have fewer than 40 properties, you should consider widening
          your search criteria.
        </li>
      </ul>
    </div>
  );
}

function DataSelectionTips() {
  return (
    <div className="space-y-4">
      <FileFormatRequirements />
      <SelectingDataTips />
    </div>
  );
}

export default function AdjustmentsIdData({
  user,
  adjustment,
  errors,
  notice,
  direct_upload_url,
  dataset_url,
}: Props) {
  const { data, isPending, error } = useFetch(dataset_url);
  const { toast } = useToast();

  const { post: proceedToNext, processing: proceedProcessing } = useForm();

  const validatedData = useMemo(() => {
    if (!data) return null;
    return validateDatasetBasedOnVersion(data);
  }, [data]);

  const version = data ? detectDatasetVersion(data) : null;
  const datasetHeaders =
    version && version === "v2"
      ? ["House Number", "Street Name", "Contract Date", "Close Price"]
      : ["Address", "Contract Date", "Close Price"];

  const errorRecords = useMemo(() => {
    if (!validatedData) return null;

    const filtered = validatedData.filter((item) => item.cells.some((cell) => cell.error));
    posthog.capture("dataset_error_records", {
      rows: filtered.length,
    });
    return filtered;
  }, [validatedData]);

  function numColsToDatasetVersion(numColsInSheet: number) {
    switch (numColsInSheet) {
      case 3:
        return "v1";
      case 4:
        return "v2";
      default:
        return null;
    }
  }

  async function setParseResults(result: CsvResult) {
    if (result.data.length >= 1500) {
      posthog.capture("dataset_too_large", {
        rows: result.data.length,
      });
      toast({
        title: "That's a lot of data!",
        description: "Teeba currently has a limit of 1500 rows.",
        variant: "destructive",
      });
      return;
    }

    const sheet: Sheet = result.data.map((row) => {
      return row.map((cell: string) => {
        return cell;
      });
    });
    const sheetWithIndexes = genIndexesOnSheet(sheet);
    const numColsInSheet = sheetWithIndexes[0].cells.length;
    const currentDatasetVersion = numColsToDatasetVersion(numColsInSheet);
    if (!currentDatasetVersion) {
      posthog.capture("dataset_invalid_file", {
        num_cols: numColsInSheet,
      });
      toast({
        title: "Invalid file",
        description:
          "Please make sure the file meets the formatting requirements described on the page.",
        variant: "destructive",
      });
      return;
    }

    const formattedSheet = autoformatFields(sheetWithIndexes, currentDatasetVersion);

    switch (currentDatasetVersion) {
      case "v1": {
        const fileData = sheetToFileV1Data(formattedSheet);
        await saveDataset(adjustment.public_id, fileData, direct_upload_url, currentDatasetVersion);

        break;
      }
      case "v2": {
        const fileData = sheetToFileV2Data(formattedSheet);
        await saveDataset(adjustment.public_id, fileData, direct_upload_url, currentDatasetVersion);
        break;
      }
    }

    posthog.capture("dataset_saved", {
      version: currentDatasetVersion,
      rows: result.data.length,
    });

    router.reload();
  }

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title={`Trend Data - ${adjustment.address}`} />
      <div className="py-4">
        <div className="flex justify-between">
          <BackToDashboard />
        </div>

        <AdjustmentSteps currentStep="data" adjustment={adjustment} className="mb-4" />

        <PropertyHeader adjustment={adjustment} />

        {isPending && <LoadingCard />}

        {!isPending && !validatedData && (
          <div className="flex space-x-8">
            <div className="space-y-4 max-w-xl mx-auto pt-4">
              <CardTitle className="text-2xl">Upload your trend data</CardTitle>

              <CsvUpload setParseResults={setParseResults} className="pt-4">
                <div className="space-y-2 text-center">
                  <p className="text-lg">
                    Drag a file here, or click to select from your computer.
                  </p>
                  <p>File format: .csv</p>
                </div>
              </CsvUpload>

              <SelectingDataTips />
            </div>
            <div className="max-w-md flex-grow">
              <div className="space-y-4 w-full">
                <StepTitleSection
                  title="Trend Data"
                  description={
                    <div className="space-y-4">
                      <div className="space-y-4">
                        <p>Select the data for your analysis.</p>
                      </div>

                      <div>
                        <Alert variant="muted">
                          <GrHelpBook className="w-5 h-5" />
                          <AlertTitle>Need some help?</AlertTitle>
                          <AlertDescription>
                            <a
                              href="https://teeba-calculator.com/instructions#upload-data"
                              className="text-primary font-bold hover:underline"
                              target="_blank"
                            >
                              Click here
                            </a>{" "}
                            to view more detailed instructions.
                          </AlertDescription>
                        </Alert>
                      </div>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    <FileFormatRequirements />
                  </div>
                </StepTitleSection>
              </div>
            </div>
          </div>
        )}

        {!isPending && validatedData && (
          <div className="flex space-x-8 pt-4">
            <div className="flex-grow">
              <div className="flex mx-auto">
                <div className="w-full">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-2xl">
                        <h1 className="text-2xl font-bold">Data</h1>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isPending ? (
                        <div className="h-96"></div>
                      ) : (
                        <div>
                          {error && <div>Error: {error}</div>}
                          {validatedData ? (
                            <div className="space-y-6">
                              <SheetTable sheet={validatedData} headers={datasetHeaders} />
                            </div>
                          ) : (
                            <CsvUpload setParseResults={setParseResults} />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
            <div className="max-w-md flex-grow">
              <div className="w-full space-y-4">
                <StepTitleSection
                  title="Trend Data"
                  description={
                    <div className="space-y-4">
                      <p>Select the data for your analysis.</p>
                      <div>
                        <Alert variant="muted">
                          <GrHelpBook className="w-5 h-5" />
                          <AlertTitle className="">Need some help?</AlertTitle>
                          <AlertDescription>
                            <a
                              href="https://teeba-calculator.com/instructions#upload-data"
                              className="text-primary font-bold hover:underline"
                              target="_blank"
                            >
                              Click here
                            </a>{" "}
                            to view more detailed instructions.
                          </AlertDescription>
                        </Alert>
                      </div>
                    </div>
                  }
                >
                  {validatedData &&
                    (errorRecords?.length === 0 ? (
                      <Button
                        onClick={() => {
                          proceedToNext(`/adjustments/${adjustment.public_id}/data/complete`, {
                            preserveScroll: true,
                            preserveState: true,
                          });
                        }}
                        className={cn(buttonVariants(), "w-full")}
                        disabled={proceedProcessing}
                      >
                        {proceedProcessing ? "Processing..." : "Next"} <ArrowRightIcon />
                      </Button>
                    ) : (
                      <Button className="w-full" disabled>
                        Next <ArrowRightIcon />
                      </Button>
                    ))}
                </StepTitleSection>

                {validatedData && (
                  <div className="space-y-4">
                    <p className="text-sm">{validatedData.length} rows in your dataset.</p>

                    {errorRecords && errorRecords.length > 0 && (
                      <div>
                        <p className="text-sm text-red-500">
                          There are {errorRecords.length} records with errors.
                        </p>
                      </div>
                    )}

                    <Button
                      variant="outline"
                      onClick={async () => {
                        await clearDataset(adjustment.public_id);
                        router.reload();
                      }}
                    >
                      Clear and upload new data
                    </Button>

                    <DataSelectionTips />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}
